export const collectionChecklist = [
  'Complete 45-minute Aqua Cardio Classes',
  'Aquatic Strength & Resistance Workouts',
  'Timed 30-minute Aqua Interval Classes',
  'Complete 45-minute Aqua Choreography Sessions',
  'Mixed-Level Classes with Three Intensity Options',
  'Full-Body 60-Minute Aqua Circuits',
  'Senior Aqua Fitness Classes',
  'Quick 5-, 10-, and 15-Minute Workout Combos',
  'Aqua Equipment Workouts',
  'Deep-Water Aqua Fitness Sessions',
  'Last-Minute Replacement Classes',
  'Step-by-step instructions, exact timings & coaching cues',
];

export const problemCards = [
  {
    variant: 'blue' as const,
    title: 'What if your next aqua fitness class was already planned?',
    paragraphs: [
      'Every session needs the right exercises, the right sequence, appropriate intensity, and a clear structure. Searching for new exercises, calculating durations, planning recovery, and creating modifications for different fitness levels takes time — especially when you teach several classes a week.',
      'What should you use for your next cardio class? How do you fill 45 minutes without running out of exercises? And what happens when you need a complete class at the last minute?',
      'AQUA CLASS HUB™ brings together complete aquatic fitness classes across six essential teaching areas — with movement instructions, exact timings, recovery periods, and intensity adaptations.',
    ],
    highlight: 'Open. Follow. Demonstrate. Teach.',
  },
  {
    variant: 'gold' as const,
    title: 'Spend less time preparing every class.',
    paragraphs: [
      'Your time should go to guiding participants, demonstrating exercises, and delivering organized sessions — not to constantly building new routines from scratch.',
      'Need cardio? Open the cardio volume. Strength? Select a complete aquatic strength class. Mixed fitness levels? Follow a session with three intensity options. Want a new format? Choose intervals, choreography, or a full-body circuit.',
    ],
    highlight: 'Less repetitive preparation. More organized aqua fitness sessions.',
  },
  {
    variant: 'navy' as const,
    title: 'Less searching. More ready-to-teach workouts.',
    paragraphs: [
      'Structured cardio with aquatic jogging, lateral steps, and knee drives. Strength sessions using controlled water resistance. Interval workouts with exact work and recovery periods. Choreography with 8-, 16-, and 32-count patterns. Mixed-level classes with three intensity options. Full 60-minute circuits with defined stations and rotations.',
      'Plus five bonuses covering senior aqua fitness, quick combos, equipment workouts, deep-water classes, and last-minute replacement sessions.',
    ],
    highlight: 'Open the class. Follow the timeline. Start teaching.',
  },
];

export const volumes = [
  {
    number: '01',
    title: 'Aqua Cardio Classes',
    subtitle: '8 Complete Ready-to-Teach 45-Minute Cardio Classes',
    points: [
      'Fully structured classes featuring water jogging, lateral movements, alternating knee drives, heel digs, underwater arm pushes, and full-body aerobic combinations.',
      'Every session includes a complete warm-up, predetermined cardio combinations, intensity modifications, active recovery, and a full cool-down — with clear instructions, exact durations, and transitions.',
    ],
    finalLine: 'Warm up. Move. Build endurance. Recover.',
    meta: '48 Practical Pages • 8 Complete Classes',
  },
  {
    number: '02',
    title: 'Aqua Strength & Resistance',
    subtitle: '8 Complete Ready-to-Teach 45-Minute Strength Classes',
    points: [
      'Controlled movement against water resistance for the legs, glutes, hips, arms, shoulders, and trunk — aquatic squats, hip extensions, lateral leg presses, and pulling movements.',
      'Detailed starting positions, exact durations, recovery periods, and intensity modifications. No specialized equipment required.',
    ],
    finalLine: 'Control. Press. Strengthen. Progress.',
    meta: '48 Practical Pages • 8 Complete Classes',
  },
  {
    number: '03',
    title: 'Aqua Interval Workouts',
    subtitle: '8 Complete Ready-to-Teach 30-Minute Interval Classes',
    points: [
      'Stop calculating intervals before every workout. Follow predetermined formats such as 30/30, 40/20, 45/15, and pyramid-style sessions.',
      'Each exercise is assigned to a specific time, so you know exactly when to start, recover, and move on.',
    ],
    finalLine: 'Start. Work. Recover. Repeat.',
    meta: '48 Practical Pages • 8 Complete Classes',
  },
  {
    number: '04',
    title: 'Aqua Choreography Classes',
    subtitle: '8 Complete Ready-to-Teach 45-Minute Choreography Classes',
    points: [
      'Structured 8-count movements, 16-count combinations, and complete 32-count phrases with musical counts, repetitions, and transitions.',
      'Every sequence explains foot placement, arm coordination, direction, and side changes — so you can focus on teaching the routine.',
    ],
    finalLine: 'Follow the counts. Connect the movements. Teach the routine.',
    meta: '48 Practical Pages • 8 Complete Classes',
  },
  {
    number: '05',
    title: 'Mixed-Level Aqua Classes',
    subtitle: '8 Complete Ready-to-Teach 45-Minute Classes',
    tagline: 'One complete class. Three intensity options.',
    points: [
      'Teaching different fitness levels without preparing separate workouts. Each main exercise includes three options:',
    ],
    levels: [
      { label: 'Lower intensity', text: 'Controlled movements, smaller ranges, and reduced physical demand.' },
      { label: 'Moderate intensity', text: 'Coordinated exercises with purposeful movement and water resistance.' },
      { label: 'Higher intensity', text: 'More demanding movements, increased resistance, or greater complexity.' },
    ],
    afterPoints: ['All three versions follow the same timeline, so the group stays organized and moves together.'],
    finalLine: 'One group. Three options. One organized session.',
    meta: '48 Practical Pages • 8 Complete Classes',
  },
  {
    number: '06',
    title: 'Full Body Aqua Circuits',
    subtitle: '8 Complete Ready-to-Teach 60-Minute Circuit Classes',
    points: [
      'Every class contains three circuits of four stations, each with two predetermined movements and detailed execution instructions.',
      'Exact working periods, transitions, active recovery, station rotations, intensity alternatives, and instructor cues.',
    ],
    finalLine: 'Set up. Rotate. Exercise. Complete the circuit.',
    meta: '64 Practical Pages • 8 Complete Classes',
  },
];

// Pages shown in the "Look Inside" collage, in order 1–5.
export const insidePages = [
  {
    image: 'https://cdn.magicpatterns.com/uploads/5B2cHYL4P7WapgGvpRbAHM/1.png',
    alt: 'Class 01 — Aqua Cardio Foundations: 45-minute class overview with timed blocks from warm-up to cool-down.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/cxnNPXTW5jL6BEuveEERGy/2.png',
    alt: '3 intensity levels for every exercise: lower, moderate, and higher options.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/41CJgyPcdTtFecCYLP5GX2/3.png',
    alt: 'Class 04 — Upper & Lower Body Cardio: complete class timeline.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/h9uEJSGBunE5vdm4u4gnQj/4.png',
    alt: 'Class 04 — Upper-Lower Foundation: timed exercise list with instructor cue.',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/rC6vvZGF2KAyeHXLjVUPcU/5.png',
    alt: 'Class 06 — Aerobic Recovery + Cool-Down: timed recovery sequence with instructor cue.',
  },
];

export const bonuses = [
  {
    number: '01',
    title: 'Senior Aqua Fitness Classes',
    subtitle: 'Complete Low-Impact Aquatic Fitness Sessions for Older Adults',
    price: 14.9,
    intro: [
      'Six complete 45-minute classes with low-impact cardio, controlled water walking, coordination, and mobility activities — including exact timing, active recovery, cool-downs, and movement modifications.',
      'Supports recreational aquatic fitness instruction; not a medical rehabilitation program.',
    ],
    finalLine: 'Move. Coordinate. Condition. Recover.',
    meta: '40 Practical Pages • 6 Complete Classes',
  },
  {
    number: '02',
    title: 'Quick Aqua Combos',
    subtitle: 'Ready-to-Teach 5-, 10-, and 15-Minute Workout Blocks',
    price: 9.9,
    intro: ['Twelve complete, ready-to-insert workout blocks for any part of your class:'],
    list: [
      'Four 5-minute aquatic exercise combinations.',
      'Four 10-minute aquatic exercise combinations.',
      'Four 15-minute aquatic exercise combinations.',
    ],
    outro: ['Each includes movement instructions, exact durations, recovery, and intensity variations.'],
    finalLine: 'Choose. Insert. Follow. Teach.',
    meta: '40 Practical Pages • 12 Workout Combos',
  },
  {
    number: '03',
    title: 'Aqua Equipment Workouts',
    subtitle: 'Complete Classes Using Aquatic Dumbbells, Pool Noodles & Kickboards',
    price: 12.9,
    intro: ['Six complete 45-minute classes using common aquatic equipment:'],
    groups: [
      { label: 'Aquatic dumbbells', text: 'Underwater arm resistance, pushing, pulling, and full-body conditioning.' },
      { label: 'Pool noodles', text: 'Coordinated movements, cardio exercises, and water-resistance activities.' },
      { label: 'Kickboards', text: 'Resistance movements, arm control, and upper- and lower-body coordination.' },
    ],
    outro: ['Includes equipment positioning, work and recovery periods, and equipment substitutions.'],
    finalLine: 'Prepare. Demonstrate. Exercise. Progress.',
    meta: '40 Practical Pages • 6 Complete Classes',
  },
  {
    number: '04',
    title: 'Deep Water Aqua Classes',
    subtitle: 'Complete Ready-to-Teach Deep-Water Fitness Sessions',
    price: 14.9,
    intro: [
      'Six complete 45-minute deep-water classes with suspended aqua jogging, coordinated leg patterns, cross-country ski movements, and full-body conditioning — using appropriate buoyancy equipment.',
      'Designed for qualified instructors in appropriately supervised aquatic facilities.',
    ],
    finalLine: 'Float. Move. Coordinate. Condition.',
    meta: '40 Practical Pages • 6 Complete Classes',
  },
  {
    number: '05',
    title: 'Last-Minute Aqua Classes',
    subtitle: 'Complete Backup Workouts for Unexpected Class Changes',
    price: 9.9,
    intro: [
      "No music, missing equipment, smaller pool space, or a shorter schedule? Instead of improvising, choose from six complete replacement classes designed for:",
    ],
    list: [
      'Music-free aquatic fitness.',
      'Equipment-unavailable situations.',
      'Reduced pool space.',
      'Unexpected mixed-level groups.',
      'Shortened class schedules.',
      'Substitute instructors.',
    ],
    finalLine: 'Select. Open. Follow. Teach.',
    meta: '40 Practical Pages • 6 Complete Classes',
  },
];

export const benefits = [
  { title: 'Complete workouts, not random exercise ideas', text: 'Every class is organized from warm-up through the final cool-down.' },
  { title: 'Practical step-by-step exercises', text: 'Complete movement instructions instead of unexplained exercise names.' },
  { title: 'Exact class durations', text: '30-, 45-, and 60-minute workouts with predetermined schedules.' },
  { title: 'Options for different fitness levels', text: 'Lower-, moderate-, and higher-intensity alternatives where appropriate.' },
  { title: 'Professional movement illustrations', text: 'Exercise images that support execution and instructor demonstrations.' },
  { title: 'Simpler class preparation', text: 'Select a complete workout that fits your group instead of building routines from scratch.' },
];

export const offerItems = [
  '6 main volumes',
  '5 free professional bonuses',
  'Aqua cardio classes',
  'Aqua strength & resistance workouts',
  'Aqua interval workouts',
  'Aqua choreography classes',
  'Mixed-level aqua classes',
  'Full body aqua circuits',
  'Senior, equipment & deep-water classes',
  'Quick combos & last-minute backup classes',
];

export const teachingSituations = [
  {
    title: 'When you teach multiple classes every week',
    text: "Complete workouts organized by training type, so you don't build every session from disconnected ideas.",
  },
  {
    title: 'When your participants have different fitness levels',
    text: 'Clear exercise modifications keep the whole group on one organized class timeline.',
  },
  {
    title: 'When you need fresh movement combinations',
    text: 'Choose cardio, choreography, interval, or full-body sessions instead of repeating the same format.',
  },
  {
    title: 'When your original class plan changes',
    text: 'Last-minute replacement sessions and short, ready-to-use combinations are always at hand.',
  },
];

export const testimonials = [
  {
    name: 'Linda H.',
    age: 52,
    role: 'Certified aqua fitness instructor · Community recreation center, Arizona',
    quote:
      'I teach nine water aerobics classes a week, and Sunday nights used to disappear into planning. Now I open a volume, pick a class, and the warm-up, timings, and cool-down are already there. When the music system failed right before class, I opened a last-minute session on my phone and taught the full 45 minutes.',
  },
  {
    name: 'Carol B.',
    age: 61,
    role: 'Water aerobics instructor · Senior living community, Florida',
    quote:
      "Most of my participants are between 65 and 80, and a few are much fitter than the rest. The mixed-level volume solved that: one timeline, three options, everybody moving together. The coaching cues are short and clear enough to call out over the splashing.",
  },
  {
    name: 'Mark S.',
    age: 44,
    role: 'Aquatic group exercise coordinator · Municipal pool, Ontario, Canada',
    quote:
      'I manage five aqua instructors, and every class used to look different. I printed the interval and circuit volumes for our staff binder, and now substitutes follow the exact work and recovery periods without improvising. Newer instructors pick up the choreography counts fast.',
  },
];

export const faqs = [
  {
    q: 'Is AQUA CLASS HUB™ a physical product?',
    a: 'No. It is a 100% digital collection of aquatic fitness instructional materials in PDF format. Nothing will be shipped.',
  },
  {
    q: 'Who is this bundle designed for?',
    a: 'Aqua fitness instructors, water aerobics instructors, and qualified fitness professionals who lead aquatic group exercise sessions.',
  },
  {
    q: 'Is this a course or a professional certification?',
    a: 'No. It is a collection of practical class plans and instructional resources. It does not provide certification or replace instructor qualifications.',
  },
  {
    q: 'Do I have to create the classes myself?',
    a: "No. The workouts include predetermined sequences, durations, and recovery periods. You'll still use your professional judgment to select suitable options for your participants.",
  },
  {
    q: 'How many complete classes are included?',
    a: '72 ready-to-teach aqua fitness classes plus 12 quick workout combos — 504 pages in total.',
  },
  {
    q: 'How long are the classes?',
    a: 'Full classes last 30, 45, or 60 minutes depending on the volume. The Quick Aqua Combos bonus adds 5-, 10-, and 15-minute blocks.',
  },
  {
    q: 'Can I use the classes with different fitness levels?',
    a: 'Yes. The collection includes intensity modifications, and Volume 05 is specifically designed for mixed-level groups.',
  },
  {
    q: 'Do I need special aquatic fitness equipment?',
    a: 'Most main volume workouts need no equipment. Bonus 03 uses dumbbells, noodles, and kickboards; Bonus 04 uses buoyancy equipment in a supervised deep-water setting.',
  },
  {
    q: 'Are the five bonuses really included free?',
    a: 'Yes. All five professional bonuses are included at no additional charge with the complete bundle for $26.90 USD.',
  },
  {
    q: 'How will I receive my purchase?',
    a: "After your order, you'll receive digital access through Bright Digital Hub's delivery process. Questions? Contact compradigitalyaa@gmail.com.",
  },
];

export const recentBuyers = [
  { name: 'Sandra Miller', place: 'Phoenix, United States', minutes: 6 },
  { name: 'Lucía Fernández', place: 'Valencia, Spain', minutes: 11 },
  { name: 'Karen Whitfield', place: 'Brisbane, Australia', minutes: 4 },
  { name: 'Mariana Costa', place: 'Lisbon, Portugal', minutes: 15 },
  { name: 'Julie Lefebvre', place: 'Montréal, Canada', minutes: 8 },
  { name: 'Helen Parker', place: 'Bristol, United Kingdom', minutes: 19 },
  { name: 'Valeria Gómez', place: 'Mexico City, Mexico', minutes: 9 },
  { name: 'Anna Schneider', place: 'Munich, Germany', minutes: 13 },
  { name: 'Rachel Kim', place: 'Auckland, New Zealand', minutes: 7 },
];
